import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from "react-router";
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import Edit from 'lucide-react/dist/esm/icons/edit';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left';

// FSD 아키텍처 기반 import
import { ReviewList, Review, CourseReviewModal } from '@edusense/features-course';
import { LoadingSpinner, ErrorDisplay, Button } from "@edusense/shared-ui-kit";
import { useAuth } from '@edusense/shared-auth-core';
import { COURSE_REVIEWS_PAGE_TEST_IDS } from '@edusense/shared-constants';

// API 클라이언트 import
import {
  useGetCourseDetail,
  useGetCourseReviews,
  CourseDetail,
  getGetCourseReviewsQueryKey
} from '@edusense/course-client';

// PageContainer 수정: 테마 배경색, 글자색, 반응형 패딩 적용
const PageContainer = styled.div(({ theme }) => ({
  padding: theme.spacing.xl, // 테마 간격 사용
  maxWidth: '900px',
  margin: '0 auto',
  backgroundColor: theme.background, // 테마 배경색
  color: theme.text, // 테마 기본 텍스트 색상
  minHeight: 'calc(100vh - 60px)', // 예시: 헤더 높이 제외한 전체 높이

  [`@media (max-width: ${theme.breakpoints.md})`]: {
    padding: theme.spacing.lg, // 중간 화면 패딩
  },
  [`@media (max-width: ${theme.breakpoints.sm})`]: {
    padding: theme.spacing.md, // 작은 화면 패딩
  },
}));

// PageHeaderContainer 수정: 반응형 정렬 및 간격 적용
const PageHeaderContainer = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing.lg, // 테마 간격 사용

  [`@media (max-width: ${theme.breakpoints.sm})`]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
}));

// 헤더 왼쪽 영역 (뒤로가기 + 제목)
const HeaderLeft = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing.md,
}));

// PageTitle 수정: 테마 글자색, 반응형 글꼴 크기 적용
const PageTitle = styled.h1(({ theme }) => ({
  fontSize: theme.typography.fontSize.xl,
  fontWeight: theme.typography.fontWeight.bold,
  color: theme.text,
  margin: 0,

  [`@media (max-width: ${theme.breakpoints.sm})`]: {
    fontSize: theme.typography.fontSize.lg,
  },
}));

const CourseReviewsPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth(); // 로그인 상태 가져오기
  const queryClient = useQueryClient(); // queryClient 가져오기

  // 리뷰 작성 모달 상태 추가
  const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);

  const reviewsQueryParams = { page: '0', size: '10' } as const;

  const {
    data: courseData,
    isLoading: courseLoading,
    error: courseError
  } = useGetCourseDetail(
    courseId!,
    {},
    {
      enabled: !!courseId,
      select: (response) => response.data // response.data만 반환
    }
  );

  // 🚀 자동 생성된 React Query 훅 사용 - 간소화!
  const {
    data: reviewsData,
    isLoading: reviewsLoading,
    error: reviewsError,
    queryKey: reviewsQueryKey
  } = useGetCourseReviews<Review[], Error>(
    courseId!,
    reviewsQueryParams,
    {
      query: {
        enabled: !!courseId,
        select: (response) => {
          console.log('🔍 [CourseReviewsPage] API Response (unwrapped):', response);
          console.log('🔍 [CourseReviewsPage] response.content:', response.content);

          const reviewResponses = response.content ?? [];
          console.log('🔍 [CourseReviewsPage] reviewResponses:', reviewResponses);

          // ReviewResponse[] -> Review[] 변환 (import한 Review 인터페이스 기준)
          const mapped = reviewResponses.map(res => {
            return {
              id: res.id,
              author: {
                id: res.studentId,
                displayName: res.studentName,
                profileImageUrl: res.studentAvatarUrl,
                username: res.studentName?.toLowerCase().replace(/\s+/g, ''),
              },
              rating: res.rating,
              comment: res.content,
              createdAt: res.createdAt,
            } satisfies Review;
          });

          console.log('🔍 [CourseReviewsPage] Mapped reviews:', mapped);
          return mapped;
        }
      }
    }
  );

  const reviews = reviewsData ?? [];

  // 수강 여부 확인 (임시로 as any 사용)
  const isEnrolled = courseData?.summary?.owned === true;

  // 리뷰 작성 버튼 클릭 핸들러
  const handleOpenComposeModal = () => {
    setIsComposeModalOpen(true);
  };

  // 모달 닫기 핸들러 (추후 모달 구현 시 사용)
  const handleCloseComposeModal = () => {
    setIsComposeModalOpen(false);
  };

  // 리뷰 제출 성공 핸들러 추가
  const handleReviewSubmitSuccess = () => {
    handleCloseComposeModal(); // 모달 닫기
    const fallbackQueryKey = getGetCourseReviewsQueryKey(courseId!, reviewsQueryParams);
    // 리뷰 목록 쿼리 무효화하여 새로고침
    queryClient.invalidateQueries({ queryKey: reviewsQueryKey ?? fallbackQueryKey });
    // TODO: 성공 토스트 메시지 표시 (react-hot-toast 사용 등)
    // toast.success('리뷰가 성공적으로 등록되었습니다!');
  };

  // 페이지 제목 설정 (강좌 정보 로딩 상태 반영)
  const getPageTitle = () => {
    if (courseLoading) return '강좌 정보 로딩 중...';
    if (courseError) return '강좌 정보를 불러올 수 없습니다.';
    if (courseData?.summary?.title) return `${courseData.summary.title} 리뷰`; 
    return '강좌 리뷰'; // 기본값
  };

  // 로딩 및 에러 상태 처리 (강좌 정보와 리뷰 목록 모두 고려)
  const isLoading = courseLoading || reviewsLoading;
  const error = courseError || reviewsError;

  if (isLoading) {
    return (
      <PageContainer data-testid={COURSE_REVIEWS_PAGE_TEST_IDS.ROOT}>
        <PageHeaderContainer>
          <HeaderLeft>
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              iconOnly
              icon={<ArrowLeft size={20} />}
              label="뒤로가기"
            />
            <PageTitle data-testid={COURSE_REVIEWS_PAGE_TEST_IDS.PAGE_TITLE}>{getPageTitle()}</PageTitle>
          </HeaderLeft>
          {/* 로딩 중에는 버튼 비활성화 또는 숨김 */}
          <Button
            variant="primary"
            disabled
            iconOnly
            icon={<Edit size={16} />}
            label="리뷰 작성"
          />
        </PageHeaderContainer>
        <LoadingSpinner data-testid={COURSE_REVIEWS_PAGE_TEST_IDS.LOADING_SPINNER} />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer data-testid={COURSE_REVIEWS_PAGE_TEST_IDS.ROOT}>
         <PageHeaderContainer>
          <HeaderLeft>
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              iconOnly
              icon={<ArrowLeft size={20} />}
              label="뒤로가기"
            />
            <PageTitle data-testid={COURSE_REVIEWS_PAGE_TEST_IDS.PAGE_TITLE}>{getPageTitle()}</PageTitle>
          </HeaderLeft>
        </PageHeaderContainer>
        <ErrorDisplay
          message={courseError?.message || reviewsError?.message || '오류가 발생했습니다.'}
          data-testid={COURSE_REVIEWS_PAGE_TEST_IDS.ERROR_DISPLAY}
        />
      </PageContainer>
    );
  }

  const hasReviews = reviews && reviews.length > 0;

  return (
    <PageContainer data-testid={COURSE_REVIEWS_PAGE_TEST_IDS.ROOT}>
      <PageHeaderContainer>
        <HeaderLeft>
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            iconOnly
            icon={<ArrowLeft size={20} />}
            label="뒤로가기"
          />
          <PageTitle data-testid={COURSE_REVIEWS_PAGE_TEST_IDS.PAGE_TITLE}>{getPageTitle()}</PageTitle>
        </HeaderLeft>
        {/* 로그인 + 수강 중일 때만 버튼 표시 */}
        {isAuthenticated && isEnrolled && (
          <Button
            variant="primary"
            onClick={handleOpenComposeModal}
            iconOnly
            icon={<Edit size={16} />}
            label="리뷰 작성"
            data-testid={COURSE_REVIEWS_PAGE_TEST_IDS.WRITE_REVIEW_BUTTON}
          >
          </Button>
        )}
      </PageHeaderContainer>

      {hasReviews ? (
        <ReviewList reviews={reviews} data-testid={COURSE_REVIEWS_PAGE_TEST_IDS.REVIEW_LIST} />
      ) : (
        <div
          style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}
          data-testid={COURSE_REVIEWS_PAGE_TEST_IDS.EMPTY_STATE}
        >
          아직 작성된 리뷰가 없습니다.
        </div>
      )}

      {/* 리뷰 작성 모달을 createPortal로 렌더링 */}
      {isComposeModalOpen && courseId && typeof document !== 'undefined' && createPortal(
        <CourseReviewModal
          courseId={courseId}
          onClose={handleCloseComposeModal}
          onSubmitSuccess={handleReviewSubmitSuccess}
          data-testid={COURSE_REVIEWS_PAGE_TEST_IDS.REVIEW_MODAL}
        />,
        document.body // 포탈 대상으로 document.body 지정
      )}
    </PageContainer>
  );
};

export default CourseReviewsPage; 
